import type { ReactNode } from 'react';

interface HbsListRowProps {
  /** DOM id for the row's clickable area. Rows repeat, so callers derive this
   *  from the item (e.g. `safe-item-row-${item.id}`) rather than hardcoding. */
  id?: string;
  title: ReactNode;
  supporting?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Right-side persistent slot. Renders OUTSIDE the row button so its
   *  children get their own click handlers without bubbling up to `onClick`. */
  actions?: ReactNode;
  /** On hover, fade in a "Launch Site" style pill on the right edge. */
  overlay?: { label: string; icon?: ReactNode };
  selected?: boolean;
  onClick?: () => void;
}

const SHELL_BASE = 'group relative flex items-stretch w-full rounded-hbs-md transition-colors';
const SHELL_SELECTED = 'bg-hbs-accent-soft border border-hbs-accent/30';
const SHELL_UNSELECTED = 'border border-transparent hover:bg-hbs-surface-alt';

const ROW_INNER = 'flex items-center gap-[14px] flex-1 min-w-0 px-3 py-[10px] text-left';

export function HbsListRow({
  id,
  title,
  supporting,
  leading,
  trailing,
  actions,
  overlay,
  selected,
  onClick,
}: HbsListRowProps): JSX.Element {
  const shellClass = `${SHELL_BASE} ${selected ? SHELL_SELECTED : SHELL_UNSELECTED}`;

  const inner = (
    <>
      {leading && (
        <span className="shrink-0 w-[34px] h-[34px] flex items-center justify-center">
          {leading}
        </span>
      )}
      <span className="relative flex-1 min-w-0">
        <span className="block text-[14px] font-semibold text-hbs-text truncate">{title}</span>
        {supporting && (
          <span className="block text-[12px] text-hbs-text-mute truncate">{supporting}</span>
        )}
        {overlay && (
          <span
            aria-hidden="true"
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 px-2.5 py-1 text-[13px] font-semibold text-hbs-text bg-hbs-surface-alt border border-hbs-border opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-hbs-sm"
          >
            {overlay.icon}
            {overlay.label}
          </span>
        )}
      </span>
      {trailing && (
        <span className={`shrink-0 ${selected ? 'text-hbs-accent' : 'text-hbs-text-mute'}`}>
          {trailing}
        </span>
      )}
    </>
  );

  const main = onClick ? (
    <button id={id} type="button" onClick={onClick} className={ROW_INNER}>
      {inner}
    </button>
  ) : (
    <div className={ROW_INNER}>{inner}</div>
  );

  return (
    <div className={shellClass}>
      {main}
      {actions && (
        <div className="flex items-center gap-1 pr-2 shrink-0">{actions}</div>
      )}
    </div>
  );
}
